import React, { useState } from 'react';
import { AppDownloads } from '@/components/FootagePullUpdate';
import { Check, FolderOpen, Download, Clock, Mail, Film, Layers, Subtitles, Palette, MessageSquareQuote, Bookmark, AlertTriangle } from 'lucide-react';

/**
 * Prepared projects, EDITORS (Ryan, 2026-10-09: "a gorgeous popup for when editors log in next with checkboxes to make
 * sure they read slow and understand before they can clear it ... make it sound like we have amazingly great news ...
 * worded as what to expect ... and very important to email me if anything is not working properly or could be better").
 *
 * What the prep puts beside an editor's footage (docs/autoprep/QUEUE.md; tools/autoprep/stage_package.py README), how it
 * reaches them (Footage Pull, the bell notice R15), what a held download looks like (rules.EDITOR_MESSAGE, the hold R25),
 * and where to send what could be better. Every sentence is checked against what an editor actually sees.
 *
 * R19: nothing an editor sees may say "auto" or suggest AI. The words here are "prepared", "ready to edit", "the prep";
 * tests/test_prepared_projects_announcement.py holds the copy to that rule.
 *
 * Four sections, each ending in a box to tick, and the button stays locked until all four are: a slow read on purpose.
 * The boxes are buttons with role="checkbox", as in SongApprovalIntro: a native checkbox cannot opt out of the global
 * 44px tap-target rule.
 */

export const FEEDBACK_EMAIL = 'hello@uncutgems.com';
export const HELD_WORDS = "We're getting this project ready. The footage download opens when it's done.";
export const READY_WORDS = 'A ready-made project for [couple] is waiting. Click Download footage again: your footage won’t download twice, and the project appears beside it.';

// The Footage Pull editors must have: backend/app/footage_app_version.py MIN_EDITOR, 3.0 since 2026-10-14 (Ryan: "make it
// required"). tests/test_prepared_projects_announcement.py checks the two agree. 2.11 was the first that fetched the
// project files; on 2026-10-09, 17 of the 19 editors who pulled in the last two weeks were on 2.7 to 2.10.
export const MIN_APP = '3.0';

const AGREE = [
  'I’ll open “README - START HERE” first, and I know the rough edit is a starting point, not the film.',
  `I\u2019ll update Footage Pull to ${MIN_APP} before my next download. It\u2019s the only way to get footage now, and a bell notice tells me if the project arrives later.`,
  'If the download says the project is being prepared, I’ll wait or message my PM: nothing is broken.',
  `If anything is off or could be better, I’ll email ${FEEDBACK_EMAIL} with the couple’s name and what I saw.`,
];

// The Footage Pull update is the one thing every editor must DO: red, set here so both themes keep it red (Ryan,
// 2026-10-09: "put a red icon or something here to draw attention"). White on this red is 4.8:1.
const ALERT_RED = '#dc2626';
const ALERT_BOX = { borderColor: 'rgba(220, 38, 38, 0.55)', background: 'rgba(220, 38, 38, 0.07)' };
const TICK_FILL = '#2bb3a8';   // the site's primary button teal (as VersionWatch's Reload)
const TICK_INK = '#0a0a0a';

function Box({ checked, onToggle, children, testid }) {
  return (
    <button type="button" role="checkbox" aria-checked={checked} onClick={onToggle} data-testid={testid}
      className={`tap-auto w-full mt-4 flex items-start gap-3 text-left rounded-xl px-3.5 py-3 border transition-colors ${
        checked ? 'border-oxidized-teal/60 bg-oxidized-teal/[0.08]' : 'border-ug-border-strong bg-ug-surface/40 hover:border-ug-gold/50'}`}>
      {/* The filled box in the site's primary teal with black ink, set here so neither theme recolors it: the light
          site look turns bg-oxidized-teal near black, and the black check vanished into it (Ryan, 2026-10-09). */}
      <span className={`mt-0.5 w-[18px] h-[18px] shrink-0 rounded-md border flex items-center justify-center transition-colors ${
        checked ? '' : 'border-ug-border-strong'}`} data-testid={`${testid}-box`}
        style={checked ? { background: TICK_FILL, borderColor: TICK_FILL } : undefined}>
        {checked && <Check className="w-3 h-3" style={{ color: TICK_INK }} strokeWidth={3.5} />}
      </span>
      <span className={`text-[12.5px] leading-relaxed ${checked ? 'text-zinc-100' : 'text-zinc-100/80'}`}>{children}</span>
    </button>
  );
}

function Section({ n, icon: Icon, title, children }) {
  return (
    <section className="px-6 sm:px-8 py-6 border-t border-ug-border-subtle">
      <div className="flex items-center gap-3 mb-3">
        <span className="font-heading text-[13px] text-ug-gold tabular-nums">{n}</span>
        <Icon className="w-4 h-4 text-oxidized-teal" strokeWidth={2} />
        <h3 className="font-heading text-[19px] leading-tight text-zinc-100">{title}</h3>
      </div>
      <div className="text-[13px] text-zinc-100/75 leading-relaxed space-y-2.5">{children}</div>
    </section>
  );
}

const ITEMS = [
  [Layers, 'Synced timelines', 'every camera and audio recorder lined up for each part of the day, stacked and uncut.'],
  [Film, 'A rough edit of the whole day', '“Full Day - Rough Edit” (on a doc edit, “Doc edit - first cut”): cuts made and dialogue balanced, in order, for finding your moments fast.'],
  [Bookmark, 'Markers on the moments', 'vows, rings, every toast, big reactions, and the client’s must-haves. If a must-have couldn’t be found, a marker says so.'],
  [MessageSquareQuote, 'Soundbites and candid moments', 'the best lines as whole sentences with every angle under them, plus the things people said that only the cameras heard.'],
  [FolderOpen, 'Bins sorted for you', 'by part of the day and by camera, drone shots on their own, and “Rejects - check before deleting” kept apart.'],
  [Palette, 'Color-match LUTs', 'one per camera, matching it to the main angle, in “Other files” to put on yourself if you want a head start on color (plus the client’s own LUT when they gave one). The timelines open ungraded.'],
  [Subtitles, 'Subtitles', 'an English subtitle file for every timeline, ready to restyle in your own fonts. Anything said in another language is translated, with the original beside it.'],
];

export function PreparedProjectsAnnouncement({ onAcknowledge }) {
  const [ticked, setTicked] = useState([false, false, false, false]);
  const [saving, setSaving] = useState(false);
  const toggle = (i) => setTicked(t => t.map((v, j) => (j === i ? !v : v)));
  const done = ticked.filter(Boolean).length;
  const ready = done === ticked.length;

  return (
    <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-ug-raised border border-ug-border shadow-2xl"
         data-testid="whats-new-prepared-projects">
      {/* the news */}
      <header className="relative overflow-hidden px-6 sm:px-8 pt-8 pb-7">
        <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-16 w-80 h-80 rounded-full bg-oxidized-teal/20 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-28 -left-20 w-80 h-80 rounded-full bg-ug-gold/15 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-4">
            <span className="font-sans text-[10px] font-semibold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full bg-oxidized-teal/15 text-oxidized-teal border border-oxidized-teal/30">
              Rolling out now
            </span>
            <span className="font-sans text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">For every editor</span>
          </div>
          <h2 className="font-heading text-[30px] sm:text-[36px] leading-[1.05] text-zinc-100 mb-3" style={{ textWrap: 'balance' }}>
            Your projects now arrive <span className="text-ug-gold">ready to edit.</span>
          </h2>
          <p className="text-[14px] text-zinc-100/80 leading-relaxed max-w-xl">
            This is the biggest change to how you work with us, and it&rsquo;s a great one. Before the footage reaches you, we now
            prepare the whole project: every angle synced, the day sorted, the moments marked and a rough edit of the day
            laid out. You open it and start editing, hours sooner.
          </p>
          <p className="text-[12.5px] text-slate-400 leading-relaxed mt-3">
            Please read this slowly. There&rsquo;s a box to tick after each part, and the button unlocks when all four are ticked.
          </p>
        </div>
      </header>

      <Section n="01" icon={FolderOpen} title="What you&rsquo;ll find beside your footage">
        <p>
          A folder named for the couple, like <span className="text-zinc-100 font-medium">&ldquo;Kristi + Frank - project files&rdquo;</span>,
          set up for the client&rsquo;s editing program (Resolve, Premiere or Final Cut). Start with
          <span className="text-zinc-100 font-medium"> &ldquo;README - START HERE&rdquo;</span>: it lists every timeline and how to open it.
        </p>
        <ul className="grid gap-2 pt-1">
          {ITEMS.map(([Icon, t, d]) => (
            <li key={t} className="flex items-start gap-2.5 rounded-lg bg-ug-surface/50 border border-ug-border px-3 py-2.5">
              <Icon className="w-4 h-4 text-oxidized-teal shrink-0 mt-0.5" strokeWidth={2} />
              <span><span className="text-zinc-100 font-medium">{t}:</span> {d}</span>
            </li>
          ))}
        </ul>
        <p>
          Every folder, timeline and marker is explained in the Editor Guide:{' '}
          <a href="/editor/guide#prepared-projects" target="_blank" rel="noreferrer" data-testid="prepared-guide-link"
             className="tap-auto text-ug-gold font-medium underline underline-offset-2">Prepared Projects</a>.
        </p>
        <p>
          The client&rsquo;s notes come too, in &ldquo;Client notes.txt&rdquo;. The rough edit is a head start, not the film: the
          story and the craft are still yours. Build the film from it and from the synced timelines.
        </p>
        <Box checked={ticked[0]} onToggle={() => toggle(0)} testid="prepared-agree-1">{AGREE[0]}</Box>
      </Section>

      <Section n="02" icon={Download} title="How it reaches you">
        <div className="rounded-xl border px-4 py-3.5 space-y-2.5" data-testid="prepared-update-app" style={ALERT_BOX}>
          <p className="flex items-center gap-2.5">
            <span className="w-7 h-7 shrink-0 rounded-full flex items-center justify-center" style={{ background: ALERT_RED }} aria-hidden="true" data-ug="badge">
              <AlertTriangle className="w-4 h-4" style={{ color: '#ffffff' }} strokeWidth={2.5} />
            </span>
            {/* data-ug="badge": the light theme's avatar rule (index.css) greys any rounded-full with an inline background */}
            <span className="text-[10px] font-semibold uppercase tracking-[0.16em] px-2 py-0.5 rounded-full" style={{ background: ALERT_RED, color: '#ffffff' }} data-ug="badge">
              Required
            </span>
          </p>
          <p className="text-[13px] text-zinc-100 leading-relaxed">
            <span className="font-semibold">First, update Footage Pull to {MIN_APP}.</span> Older copies can&rsquo;t download
            footage any more. On a Mac, drag the new app into Applications and choose Replace; on Windows, unzip it
            in place of the old folder. Your login and any half-finished downloads carry over.
          </p>
          <AppDownloads />
        </div>
        <p data-testid="prepared-only-way">
          <span className="text-zinc-100 font-semibold">Footage Pull is now the only way to get footage.</span> MediaShuttle and the
          NAS link are gone from the Files tab. When a client mails us a hard drive, we upload it to our Dropbox, and it comes
          through Footage Pull like everything else.
        </p>
        <p>
          Then nothing new to learn. Download footage in Footage Pull as always. When the footage is complete, the project
          files land right beside it and the app shows <span className="text-zinc-100 font-medium">Open project</span>. The
          timelines already link to the footage you just pulled.
        </p>
        <p>
          If you downloaded the footage before the project was ready, you&rsquo;ll get a bell notice in the portal:
        </p>
        <blockquote className="rounded-lg border-l-2 border-ug-gold bg-ug-surface/50 px-3.5 py-2.5 text-[12.5px] text-zinc-100/85 italic">
          {READY_WORDS}
        </blockquote>
        <Box checked={ticked[1]} onToggle={() => toggle(1)} testid="prepared-agree-2">{AGREE[1]}</Box>
      </Section>

      <Section n="03" icon={Clock} title="If the download is waiting">
        <p>
          While we prepare a project, the Download footage button is replaced by this line:
        </p>
        <blockquote className="rounded-lg border-l-2 border-oxidized-teal bg-ug-surface/50 px-3.5 py-2.5 text-[12.5px] text-oxidized-teal">
          {HELD_WORDS}
        </blockquote>
        <p>
          That&rsquo;s expected, and nothing is broken. It usually takes a few hours, and never more than a day and a half
          after the job reaches you; then the download opens by itself. The Footage Pull app may show the same words after
          &ldquo;HTTP 423&rdquo;. If your deadline is tight, message your PM in chat and we can open it for you right away.
        </p>
        <Box checked={ticked[2]} onToggle={() => toggle(2)} testid="prepared-agree-3">{AGREE[2]}</Box>
      </Section>

      <Section n="04" icon={Mail} title="Help us make it even better">
        <p>
          You&rsquo;re the first editors to work this way, and your eye makes it better for everyone. If anything doesn&rsquo;t work,
          or could be better, tell us: an angle out of sync, a moment we missed, a marker in the wrong place, a cut that feels
          wrong, a subtitle that misheard, a color-match LUT that looks off.
        </p>
        <div className="rounded-xl border border-ug-gold/40 bg-ug-gold/[0.07] px-4 py-3">
          <p className="text-[13px] text-zinc-100 leading-relaxed">
            Email <a href={`mailto:${FEEDBACK_EMAIL}`} className="tap-auto font-semibold text-ug-gold underline underline-offset-2">{FEEDBACK_EMAIL}</a> with
            the couple&rsquo;s name and what you saw. A screenshot or a timecode helps. Every note improves the next project.
          </p>
        </div>
        <Box checked={ticked[3]} onToggle={() => toggle(3)} testid="prepared-agree-4">{AGREE[3]}</Box>
      </Section>

      <footer className="sticky bottom-0 px-6 sm:px-8 py-5 border-t border-ug-border-subtle bg-ug-raised/95 backdrop-blur">
        <button type="button" disabled={!ready || saving} data-testid="whats-new-next"
          onClick={async () => { setSaving(true); try { await onAcknowledge?.(); } finally { setSaving(false); } }}
          className="tap-auto w-full py-3 rounded-lg bg-gold-leaf text-obsidian font-sans text-[11px] font-semibold uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
          {saving ? 'Saving…' : ready ? 'I’m ready to edit' : `Tick all four boxes to continue (${done} of 4)`}
        </button>
      </footer>
    </div>
  );
}

export default PreparedProjectsAnnouncement;
